import styled, { css } from 'styled-components';

import { LAYERS } from 'components/constants/layers';
import { MOBILE_TABBAR_HEIGHT, mq } from 'components/constants/breakpoints';
import { T } from 'services/ThemeTokens';

import * as Q from './TaskQuick.styled';
import {
  BUCKET_TONES,
  chipTone,
  ICardEdgeTone,
  ITaskTone,
  PLAIN_TONE,
  SURFACE,
} from './taskTones';

/* Цвета живут в `taskTones.ts` — одни и те же для доски, списка и Ганта.
 * Прежде каждый из трёх файлов держал свои литералы, и один признак
 * («в работе», «высокий приоритет») успел разъехаться на два цвета. */

export const Card = styled.div<{ $fill?: boolean; $fab?: boolean }>`
  border: 1px solid ${SURFACE.line};
  border-radius: 10px;
  /* Серый фон рабочей области: белая доска с почти белыми колонками
     (#f8fafc) не давала глазу ни одной границы, кроме тонкой рамки. */
  background: ${SURFACE.app};
  /* 12 → 8px сверху: в режиме fill рамка снята, а отступ оставался, и каждый
     пиксель здесь — пиксель, отнятый у полотна задач (ТЗ 0064). */
  padding: 8px 16px 12px;

  /* ТЗ 0121: на телефоне поля 12 px — как у страницы. */
  ${mq.mobile} {
    /* Под плавающей «＋» — запас, чтобы она не закрывала последнюю задачу. */
    padding: 8px 12px ${({ $fab }) => ($fab ? '88px' : '12px')};
  }

  ${({ $fill }) =>
    $fill &&
    css`
      border: 0;
      border-radius: 0;
      height: 100%;
      min-height: 0;
      display: flex;
      flex-direction: column;
    `}
`;

/** Wraps the board (+ closed strip) so it can flex-fill the card in fill mode. */
export const BoardArea = styled.div<{ $fill?: boolean }>`
  ${({ $fill }) =>
    $fill &&
    css`
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    `}
`;

export const Muted = styled.div`
  font-size: 13px;
  color: var(--k-fg-64748b);
  padding: 16px 0;
`;

/* --- Create form + toolbar --- */

export const TaskForm = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 12px;
`;

export const Input = styled.input`
  box-sizing: border-box;
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
`;

export const TitleInput = styled(Input)`
  flex: 1;
  min-width: 180px;
`;

export const DescInput = styled.textarea`
  flex: 1 0 100%;
  box-sizing: border-box;
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
  resize: vertical;
  min-height: 34px;
`;

export const Select = styled.select`
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 8px;
  padding: 7px 8px;
  font: inherit;
  font-size: 13px;
  background: var(--k-bg-ffffff);
  max-width: 100%;

  /* ТЗ 0121: 16 px — иначе iOS приближает страницу при фокусе. */
  ${mq.mobile} {
    min-height: 40px;
    font-size: 16px;
  }
`;

export const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--k-border-2563eb);
  background: var(--k-bg-2563eb);
  color: var(--k-fg-ffffff);
  border-radius: 8px;
  /* Тулбар обязан помещаться в одну строку: до 0064 контролам нужно было
     1977px на полосе в 1578, и наружу выдавливался переключатель видов. */
  padding: 6px 12px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: var(--k-bg-1d4ed8);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  svg {
    display: block;
  }

  /* ТЗ 0121: на телефоне создание — плавающая «＋» («Fab»), кнопка в панели
     дублировала бы её и съедала строку. */
  &[data-fab-twin='true'] {
    ${mq.mobile} {
      display: none;
    }
  }
`;

/**
 * Плавающая «＋» (ТЗ 0121) — создание задачи на телефоне. Стоит над панелью
 * вкладок и safe-area: иначе её закрыла бы панель или полоса «домой» iPhone.
 * Только на телефоне: на компьютере есть кнопка в панели.
 */
export const Fab = styled.button`
  display: none;

  ${mq.mobile} {
    position: fixed;
    right: 16px;
    bottom: calc(
      ${MOBILE_TABBAR_HEIGHT}px + env(safe-area-inset-bottom) + 16px
    );
    z-index: ${LAYERS.CONTENT_MAX};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border: 0;
    border-radius: 50%;
    background: ${T.accent};
    color: ${T.onAccent};
    box-shadow: ${T.shadow};
    cursor: pointer;

    &:active {
      background: ${T.accentHover};
    }

    &:focus-visible {
      outline: 2px solid ${T.accent};
      outline-offset: 3px;
    }
  }
`;

/** Full-width «add task» button at the bottom of a column, after the tasks —
 *  scroll to the last card and it's always there, clearly inviting a new task. */
export const ColAddRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin-top: 2px;
  border: 1px dashed var(--k-border-cbd5e1);
  background: transparent;
  color: var(--k-fg-64748b);
  border-radius: 8px;
  padding: 9px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;

  &:hover {
    background: var(--k-bg-eff6ff);
    border-color: var(--k-border-2563eb);
    color: var(--k-fg-2563eb);
  }

  svg {
    display: block;
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  /* 200 → 150: поле тянется само, а его минимум был третьей по величине
     статьёй расхода в переполненной строке. */
  min-width: 150px;
  box-sizing: border-box;
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 8px;
  padding: 6px 9px;
  font: inherit;
  font-size: 13px;
  color: var(--k-fg-0f172a);

  /* ТЗ 0121: на телефоне — своя строка, 40 px, шрифт 16 px (без зума iOS). */
  ${mq.mobile} {
    flex: 1 1 100%;
    min-width: 0;
    min-height: 40px;
    font-size: 16px;
  }
`;

export const Toggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--k-fg-64748b);
  cursor: pointer;
  white-space: nowrap;
`;

export const TaskBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

export const Segment = styled.div`
  display: inline-flex;
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 8px;
  overflow: hidden;
  max-width: 100%;
`;

export const SegmentBtn = styled.button<{ active?: boolean }>`
  border: 0;
  background: ${({ active }) =>
    active ? 'var(--k-bg-2563eb)' : 'var(--k-bg-ffffff)'};
  color: ${({ active }) =>
    active ? 'var(--k-fg-ffffff)' : 'var(--k-fg-64748b)'};
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 11px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  & + & {
    border-left: 1px solid var(--k-border-e2e8f0);
  }

  /* ТЗ 0121: зона нажатия на телефоне — 40 px. */
  ${mq.mobile} {
    min-height: 40px;
    min-width: 40px;
    justify-content: center;
    padding: 0 12px;
    white-space: nowrap;
  }
`;

/* --- Column board --- */

export const Board = styled.div<{ $fill?: boolean; $cols?: number }>`
  /* Равные колонки без собственного минимума: 1280 - 32 (поля карточки) =
     1248, (1248 - 4x10 отступа) / 5 = 241.6px на колонку в самом тесном
     случае — доске сроков. Пиксельный минимум здесь (прежний
     minmax(272px, 1fr)) требовал 1408px и держал доску в вечной
     горизонтальной прокрутке. Ниже брейкпоинта колонки складываются в столбик.

     Число колонок приходит пропом: доска статусов даёт три, и жёсткая пятёрка
     оставляла справа пустую треть экрана. */
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols || 5}, minmax(0, 1fr));
  gap: 10px;
  align-items: stretch;
  overflow-x: hidden;
  padding-bottom: 4px;

  @media (max-width: 1279px) {
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow-y: auto;
  }

  ${({ $fill }) =>
    $fill &&
    css`
      flex: 1;
      min-height: 0;
    `}
`;

export const Column = styled.div<{ $fill?: boolean; over?: boolean }>`
  min-width: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ over }) => (over ? SURFACE.accentLine : SURFACE.line)};
  border-radius: 10px;
  background: ${({ over }) => (over ? SURFACE.accentSoft : SURFACE.column)};
  transition: background 0.12s, border-color 0.12s;

  ${({ $fill }) =>
    $fill &&
    css`
      min-height: 0;
      max-height: 100%;
    `}
`;

export const ColumnHead = styled.div<{ $tone?: ITaskTone }>`
  display: flex;
  align-items: center;
  gap: 8px;
  /* 10 → 7px: полосу от тулбара до первой карточки мерили в браузере —
     65px уходило на подпись из одного слова (ТЗ 0064). */
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-top-left-radius: 9px;
  border-top-right-radius: 9px;
  /* Тон приходит объектом и есть у КАЖДОЙ колонки. Раньше он был
     необязательным, и цвет достался двум колонкам из пяти: три оставшиеся
     не отличались друг от друга ничем, кроме подписи. */
  color: ${({ $tone }) => ($tone || PLAIN_TONE).fg};
  background: ${({ $tone }) => ($tone || PLAIN_TONE).bg};
  border-bottom: 2px solid ${({ $tone }) => ($tone || PLAIN_TONE).line};
`;

export const ColumnBody = styled.div<{ $fill?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 10px;
  min-height: 48px;

  ${({ $fill }) =>
    $fill &&
    css`
      flex: 1;
      min-height: 0;
      overflow-y: auto;
    `}
`;

export const ColumnEmpty = styled.div`
  font-size: 12px;
  color: var(--k-fg-5b6775);
  text-align: center;
  padding: 14px 0;
  border: 1px dashed ${SURFACE.lineStrong};
  border-radius: 8px;
`;

export const NoDateDivider = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--k-fg-5b6775);
  border-top: 1px dashed var(--k-border-cbd5e1);
  padding-top: 6px;
  margin-top: 2px;
`;

export const BucketCount = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: var(--k-fg-64748b);
  background: var(--k-bg-f1f5f9);
  border-radius: 999px;
  padding: 1px 8px;
`;

/* --- Task card --- */

/**
 * Ручка перетаскивания карточки.
 *
 * ТЗ 0063: была иконкой 16×18 цветом `#cbd5e1` — попасть в неё мышью почти
 * невозможно, и владелец решил, что перетаскивание между колонками не
 * работает, хотя оно работает. Ручка остаётся ОТДЕЛЬНЫМ элементом (корпус
 * карточки тянуть нельзя — react-beautiful-dnd отказывается начинать
 * перетаскивание с вложенных контролов, из-за чего её и вынесли), но теперь
 * её видно.
 */
export const CardGrip = styled.button`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 0;
  border-radius: 6px;
  background: none;
  padding: 0;
  cursor: grab;
  color: var(--k-fg-94a3b8);
  transition: color 0.12s ease, background 0.12s ease;

  &:hover,
  &:focus-visible {
    color: var(--k-fg-1d4ed8);
    background: var(--k-bg-eff6ff);
  }

  &:active {
    cursor: grabbing;
  }
`;

export const CardBox = styled.div<{
  done?: boolean;
  dragging?: boolean;
  /** Кромка и фон по сроку (ТЗ 0109) — из `cardEdgeTone`. */
  $edge?: ICardEdgeTone;
}>`
  position: relative;
  border: 1px solid
    ${({ dragging }) =>
      dragging ? 'var(--k-border-2563eb)' : 'var(--k-border-e2e8f0)'};
  ${({ $edge, dragging }) =>
    $edge
      ? css`
          border-left: 3px solid
            ${dragging ? 'var(--k-border-2563eb)' : $edge.edge};
        `
      : ''}
  border-radius: 8px;
  background: ${({ $edge }) => $edge?.fill ?? 'var(--k-bg-ffffff)'};
  padding: 8px 10px;
  opacity: ${({ done }) => (done ? 0.65 : 1)};
  box-shadow: ${({ dragging }) =>
    dragging ? '0 8px 24px var(--k-shadow-0f172a-a180)' : 'none'};
  transition: box-shadow 0.12s ease, border-color 0.12s ease;

  &:hover {
    border-color: var(--k-border-93c5fd);
    box-shadow: 0 2px 8px var(--k-shadow-2563eb-a120);
  }

  /* Карточка при наведении сама показывает, что её можно тянуть. */
  &:hover ${CardGrip} {
    color: var(--k-fg-475569);
  }

  /* Быстрые действия появляются и по наведению, и по клавиатурному фокусу:
     ряд, доступный только мыши, для клавиатуры не существует. */
  &:hover ${Q.Actions}, &:focus-within ${Q.Actions} {
    visibility: visible;
    opacity: 1;
  }
`;

/**
 * Обложка карточки — первая картинка задачи (ТЗ 0109). Высота фиксирована:
 * карточки разной высоты из-за пропорций картинок разваливали бы колонку.
 */
export const CardCover = styled.img`
  display: block;
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 6px;
  margin-bottom: 6px;
  background: ${SURFACE.column};
`;

/** Заглушка обложки той же высоты, пока ссылка в пути. */
export const CardCoverPlaceholder = styled.div`
  height: 120px;
  border-radius: 6px;
  margin-bottom: 6px;
  background: ${SURFACE.column};
`;

/** Превью описания под названием (ТЗ 0109): отступ — под ручку и чекбокс,
 *  чтобы текст начинался ровно под названием. */
export const CardPreview = styled.div`
  margin: 2px 0 0 52px;
`;

export const CardDesc = styled.div`
  margin-top: 4px;
  color: var(--k-fg-475569);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;

  /* Scale MessageBody down for the compact card preview. */
  & > div {
    font-size: 12px;
    line-height: 1.45;
  }
`;

export const FromMsgTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
  padding: 1px 7px;
  color: var(--k-fg-5b21b6);
  background: var(--k-bg-ede9fe);

  svg {
    display: block;
  }
`;

export const CardFooter = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
`;

export const AssigneeButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  border: 1px solid var(--k-border-e2e8f0);
  background: var(--k-bg-ffffff);
  border-radius: 999px;
  padding: 2px 8px 2px 2px;
  font: inherit;
  font-size: 11px;
  font-weight: 600;
  color: var(--k-fg-475569);
  cursor: pointer;

  &:hover {
    background: var(--k-bg-f4f6fa);
    border-color: var(--k-border-cbd5e1);
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    display: block;
    color: var(--k-fg-5b6775);
    flex-shrink: 0;
  }
`;

export const AssigneeStatic = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: var(--k-fg-64748b);

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const CardHead = styled.div`
  display: grid;
  /* Первая колонка — под ручку 24px (ТЗ 0063). */
  grid-template-columns: 24px 16px minmax(0, 1fr);
  align-items: start;
  gap: 6px;

  /* ТЗ 0121: на сенсоре перетаскивания нет — нет и ручки. */
  ${mq.touch} {
    grid-template-columns: 22px minmax(0, 1fr);

    ${CardGrip} {
      display: none;
    }
  }
`;

export const CardCheckbox = styled.input`
  position: relative;
  z-index: 1;
  width: 15px;
  height: 15px;
  margin: 2px 0 0;
  cursor: pointer;
  accent-color: var(--k-fg-2563eb);

  ${mq.touch} {
    width: 22px;
    height: 22px;
    margin: 0;
  }
`;

export const CardTitle = styled.button<{ done?: boolean }>`
  border: 0;
  background: none;
  padding: 0;
  text-align: left;
  font: inherit;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: var(--k-fg-0f172a);
  text-decoration: ${({ done }) => (done ? 'line-through' : 'none')};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  /* Stretched link — the whole card opens the drawer, yet the card itself is
     not a control and holds no nested ones. */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 8px;
  }

  &:focus-visible::after {
    outline: 2px solid var(--k-border-2563eb);
    outline-offset: -2px;
  }
`;

export const CardCheck = styled.button<{ done?: boolean }>`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1px solid
    ${({ done }) =>
      done ? 'var(--k-border-3cc13b)' : 'var(--k-border-cbd5e1)'};
  background: ${({ done }) =>
    done ? 'var(--k-bg-3cc13b)' : 'var(--k-bg-ffffff)'};
  color: ${({ done }) => (done ? 'var(--k-fg-ffffff)' : 'var(--k-fg-94a3b8)')};
  font-size: 12px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  &:hover {
    border-color: var(--k-border-3cc13b);
    color: ${({ done }) =>
      done ? 'var(--k-fg-ffffff)' : 'var(--k-fg-3cc13b)'};
  }
`;

export const CardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-size: 11px;
  color: var(--k-fg-64748b);
`;

export const CardDue = styled.span<{ overdue?: boolean }>`
  font-variant-numeric: tabular-nums;
  font-weight: ${({ overdue }) => (overdue ? 700 : 500)};
  color: ${({ overdue }) =>
    overdue ? BUCKET_TONES.overdue.fg : SURFACE.muted};
`;

/** `accent` — «в работе»; тон берётся из карты статусов, чтобы карточка и
 *  строка списка не разошлись (они уже расходились: #1e3a8a против #1d4ed8). */
export const Chip = styled.span<{ tone?: 'warn' | 'accent' | 'high' }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 999px;
  padding: 1px 8px;
  color: ${({ tone }) => chipTone(tone).fg};
  background: ${({ tone }) => chipTone(tone).bg};
`;

/* --- Closed strip below the board --- */

export const DoneStrip = styled.div`
  margin-top: 12px;
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 10px;
  background: var(--k-bg-f8fafc);
  /* In fill mode the board is flex:1 — without this the strip gets pushed out
   * of the (overflow:hidden) board area and «show closed» looks like a no-op. */
  flex-shrink: 0;
`;

export const DoneStripHead = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 0;
  background: transparent;
  padding: 10px 12px;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--k-fg-475569);
  cursor: pointer;
  text-align: left;
`;

export const DoneStripBody = styled.div<{ $fill?: boolean }>`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 8px;
  padding: 0 10px 10px;

  ${({ $fill }) =>
    $fill &&
    css`
      max-height: 34vh;
      overflow-y: auto;
    `}
`;

/* --- Details drawer --- */

export const DrawerTitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

export const DrawerTaskTitle = styled.h3`
  font-size: 16px;
  font-weight: 650;
  color: var(--k-fg-0f172a);
  margin: 0 0 12px;
`;

export const DrawerSection = styled.div`
  padding: 12px 0;
  border-top: 1px solid var(--k-border-e2e8f0);
`;

export const DrawerLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--k-fg-64748b);
  margin-bottom: 6px;
`;

export const DrawerDescription = styled.div`
  font-size: 13px;
  color: var(--k-fg-0f172a);
  white-space: pre-wrap;
  word-break: break-word;
`;

export const MetaRow = styled.div`
  display: flex;
  gap: 10px;
  font-size: 13px;
  padding: 3px 0;

  & > span:first-child {
    color: var(--k-fg-64748b);
    min-width: 110px;
    flex-shrink: 0;
  }
`;

export const MetaLink = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  font: inherit;
  font-size: 13px;
  color: var(--k-fg-2563eb);
  cursor: pointer;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`;

export const DrawerFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 16px;
`;

export const EditLink = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: var(--k-fg-2563eb);
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

/* --- Redesigned details drawer --- */

// Status pill in the drawer header, coloured by status.
export const DrawerStatusPill = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  padding: 2px 10px;
  color: ${({ status }) =>
    status === 'done'
      ? 'var(--k-fg-047857)'
      : status === 'in_progress'
      ? 'var(--k-fg-1d4ed8)'
      : status === 'cancelled'
      ? 'var(--k-fg-64748b)'
      : 'var(--k-fg-475569)'};
  background: ${({ status }) =>
    status === 'done'
      ? 'var(--k-bg-d1fae5)'
      : status === 'in_progress'
      ? 'var(--k-bg-dbeafe)'
      : status === 'cancelled'
      ? 'var(--k-bg-f1f5f9)'
      : 'var(--k-bg-e2e8f0)'};
`;

// Label + inline action (e.g. «Изменить») on one row.
export const FieldHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`;

export const EditBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid var(--k-border-e2e8f0);
  background: var(--k-bg-ffffff);
  color: var(--k-fg-2563eb);
  border-radius: 8px;
  padding: 4px 10px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;

  &:hover {
    background: var(--k-bg-eff6ff);
    border-color: var(--k-border-93c5fd);
  }

  svg {
    display: block;
  }
`;

// Assignee control: avatar + a select that shows the CURRENT assignee selected.
export const AssigneeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const AssigneeSelect = styled.select`
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  color: var(--k-fg-0f172a);
  background: var(--k-bg-ffffff);
  cursor: pointer;

  &:hover {
    border-color: var(--k-border-93c5fd);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

// Readable description card (rendered markdown) + its empty state.
export const DescBox = styled.div`
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 10px;
  background: var(--k-bg-f8fafc);
  padding: 12px 14px;
  color: var(--k-fg-0f172a);
  word-break: break-word;
`;

export const DescEmpty = styled.div`
  border: 1px dashed var(--k-border-cbd5e1);
  border-radius: 10px;
  background: var(--k-bg-ffffff);
  padding: 16px;
  text-align: center;
  font-size: 13px;
  color: var(--k-fg-5b6775);
`;

export const DescActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
`;

// Compact key/value grid for the low-priority meta (author + timestamps).
export const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 7px 16px;
  font-size: 12px;
`;

export const MetaKey = styled.span`
  color: var(--k-fg-5b6775);
  white-space: nowrap;
`;

export const MetaVal = styled.span`
  color: var(--k-fg-64748b);
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

// Explicit status control in the drawer header (replaces the «Готово» button —
// marking done is just picking the «Выполнена» segment).
export const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

export const StatusSeg = styled.div`
  display: inline-flex;
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 8px;
  overflow: hidden;
`;

export const StatusSegBtn = styled.button<{ active: boolean; status: string }>`
  border: 0;
  padding: 6px 14px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  color: ${({ active }) =>
    active ? 'var(--k-fg-ffffff)' : 'var(--k-fg-64748b)'};
  background: ${({ active, status }) =>
    !active
      ? 'var(--k-bg-ffffff)'
      : status === 'done'
      ? 'var(--k-bg-059669)'
      : status === 'in_progress'
      ? 'var(--k-bg-2563eb)'
      : 'var(--k-bg-64748b)'};

  & + & {
    border-left: 1px solid var(--k-border-e2e8f0);
  }

  &:disabled {
    cursor: default;
  }

  &:hover:not(:disabled) {
    color: ${({ active }) =>
      active ? 'var(--k-fg-ffffff)' : 'var(--k-fg-0f172a)'};
  }
`;

// Two controls side by side (assignee+due, group+student).
export const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
`;

// «Open the student card» link under the student select.
export const StudentLinkRow = styled.div`
  margin-top: 6px;
  font-size: 12px;
`;

// Footer with a single, clearly-destructive delete (no Save-looking primary).
export const DrawerFooterEnd = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  margin-top: 4px;
  border-top: 1px solid var(--k-border-e2e8f0);
`;

export const DeleteBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--k-fg-dc2626);
  border-radius: 8px;
  padding: 6px 12px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;

  &:hover:not(:disabled) {
    background: var(--k-bg-fef2f2);
    border-color: var(--k-border-fecaca);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  svg {
    display: block;
  }
`;
